<div class="flow-chart-wrapper">
	<!-- Zoom Controls -->
	<div class="zoom-controls">
		<button class="zoom-button" (click)="zoomOut()" [disabled]="zoomLevel <= 30" matTooltip="Zoom Out">
			<mat-icon>remove</mat-icon>
		</button>
		<div class="zoom-level">{{ zoomLevel }}%</div>
		<button class="zoom-button" (click)="zoomIn()" [disabled]="zoomLevel >= 200" matTooltip="Zoom In">
			<mat-icon>add</mat-icon>
		</button>
		<button class="zoom-button" (click)="resetZoom()" matTooltip="Reset Zoom (100%)">
			<mat-icon>zoom_out_map</mat-icon>
		</button>
		<button class="zoom-button" (click)="fitToScreen()" matTooltip="Fit to Screen (75%)">
			<mat-icon>fullscreen</mat-icon>
		</button>
	</div>

	<div class="flow-chart-container" [class.has-details-open]="showDetails">
		<div
			class="flow-chart-content"
			*ngIf="flowNodes.length > 0"
			[style.transform]="'scale(' + zoomLevel / 100 + ')'"
			[style.transform-origin]="'top center'"
		>
			<!-- Start Node -->
			<div class="flow-node start-node">
				<mat-icon>play_arrow</mat-icon>
				<span>Form Start</span>
			</div>

			<!-- Field Nodes -->
			<div class="flow-tree">
				<ng-container *ngFor="let node of flowNodes">
					<ng-container *ngTemplateOutlet="nodeTemplate; context: { node: node }"></ng-container>
				</ng-container>
			</div>

			<!-- End Node -->
			<div class="flow-node end-node">
				<mat-icon>check_circle</mat-icon>
				<span>Form End</span>
			</div>
		</div>

		<!-- Empty State -->
		<div class="empty-state" *ngIf="flowNodes.length === 0">
			<mat-icon>list_alt</mat-icon>
			<p>No fields to visualize</p>
			<span>Add fields to the form to see the flow chart</span>
		</div>

		<!-- Detail Panel -->
		<div class="detail-panel" [class.open]="showDetails" *ngIf="selectedNode">
			<div class="detail-header">
				<div class="detail-title">
					<mat-icon>info</mat-icon>
					<span>Field Details</span>
				</div>
				<button class="close-button" (click)="closeDetails()">
					<mat-icon>close</mat-icon>
				</button>
			</div>

			<div class="detail-content">
				<!-- Node Type Badge -->
				<div class="detail-section">
					<div class="section-label">Node Type</div>
					<div class="node-type-badge" [class]="selectedNode.nodeType">
						{{ selectedNode.nodeType }}
					</div>
				</div>

				<!-- Field Information -->
				<div
					class="detail-section"
					*ngIf="selectedNode.nodeType === 'field' || selectedNode.nodeType === 'decision'"
				>
					<div class="section-label">Basic Information</div>
					<div class="info-grid">
						<div class="info-item">
							<span class="info-label">Label:</span>
							<span class="info-value">{{ selectedNode.label }}</span>
						</div>
						<div class="info-item" *ngIf="selectedNode.key">
							<span class="info-label">Key:</span>
							<span class="info-value code">{{ selectedNode.key }}</span>
						</div>
						<div class="info-item" *ngIf="selectedNode.fieldType">
							<span class="info-label">Type:</span>
							<span class="info-value">{{ selectedNode.fieldType }}</span>
						</div>
						<div class="info-item" *ngIf="selectedNode.isRepeater">
							<span class="info-label">Repeater:</span>
							<span class="info-value"> <mat-icon class="inline-icon">check_circle</mat-icon> Yes </span>
						</div>
					</div>
				</div>

				<!-- Condition Information -->
				<div class="detail-section" *ngIf="selectedNode.hideExpression">
					<div class="section-label">
						<mat-icon>device_hub</mat-icon>
						Conditional Expression
					</div>
					<div class="code-block">
						{{ selectedNode.hideExpression }}
					</div>
					<div class="info-note">
						<mat-icon>info_outline</mat-icon>
						When this expression is <strong>true</strong>, the field is <strong>hidden</strong>
					</div>
				</div>

				<!-- Field Properties -->
				<div class="detail-section" *ngIf="selectedNode.fieldConfig?.props">
					<div class="section-label">Field Properties</div>
					<div class="info-grid">
						<div class="info-item" *ngFor="let prop of selectedNode.fieldConfig.props | keyvalue">
							<span class="info-label">{{ prop.key }}:</span>
							<span class="info-value" [class.code]="typeof prop.value !== 'boolean'">
								{{ prop.value === true ? '✓' : prop.value === false ? '✗' : prop.value }}
							</span>
						</div>
					</div>
				</div>

				<!-- Validators -->
				<div class="detail-section" *ngIf="hasValidators(selectedNode)">
					<div class="section-label">
						<mat-icon>verified</mat-icon>
						Validators
					</div>
					<div class="info-note">
						<mat-icon>check_circle</mat-icon>
						This field has validation rules configured
					</div>
				</div>

				<!-- Expressions -->
				<div class="detail-section" *ngIf="hasExpressions(selectedNode)">
					<div class="section-label">
						<mat-icon>functions</mat-icon>
						Expressions
					</div>
					<div class="info-note">
						<mat-icon>check_circle</mat-icon>
						This field has dynamic expressions configured
					</div>
				</div>

				<!-- Child Fields -->
				<div class="detail-section" *ngIf="selectedNode.children && selectedNode.children.length > 0">
					<div class="section-label">
						<mat-icon>list_alt</mat-icon>
						Nested Fields
					</div>
					<div class="info-note">
						<mat-icon>info_outline</mat-icon>
						Contains {{ selectedNode.children.length }} nested field(s)
					</div>
				</div>

				<!-- Raw Configuration -->
				<div class="detail-section">
					<div class="section-label">
						<mat-icon>code</mat-icon>
						Raw Configuration
					</div>
					<div class="code-block scrollable">
						<pre>{{ getFieldConfigJson(selectedNode) }}</pre>
					</div>
				</div>
			</div>
		</div>
	</div>
</div>

<!-- Node Template (Recursive) -->
<ng-template #nodeTemplate let-node="node">
	<!-- Decision Node -->
	<div class="decision-node-wrapper" *ngIf="node.nodeType === 'decision'">
		<div class="flow-connector-vertical"></div>
		<div class="decision-node" [class.selected]="isNodeSelected(node)" (click)="selectNode(node)">
			<div class="diamond-shape">
				<mat-icon>help_outline</mat-icon>
			</div>
			<div class="decision-label" [matTooltip]="node.hideExpression || ''">
				{{ node.label }}
			</div>
		</div>
	</div>

	<!-- Branch Container -->
	<div class="branch-container" *ngIf="node.nodeType === 'branch-container'">
		<div class="flow-connector-vertical"></div>
		<div class="branch-split">
			<!-- True Branch (Left) - Field is Hidden -->
			<div class="branch-path true-branch">
				<div class="branch-label">
					<span class="label-text">Hidden</span>
					<span class="label-subtitle">(condition true)</span>
				</div>
				<div class="branch-content empty-branch">
					<div class="empty-branch-message">
						<mat-icon>visibility_off</mat-icon>
						<span>Field not shown</span>
					</div>
				</div>
			</div>

			<!-- False Branch (Right) - Field is Shown -->
			<div class="branch-path false-branch">
				<div class="branch-label">
					<span class="label-text">Shown</span>
					<span class="label-subtitle">(condition false)</span>
				</div>
				<div class="branch-content">
					<ng-container *ngFor="let branchNode of node.falseBranch">
						<ng-container
							*ngTemplateOutlet="fieldNodeTemplate; context: { node: branchNode }"
						></ng-container>
					</ng-container>
				</div>
			</div>
		</div>
		<div class="branch-merge">
			<div class="merge-icon">
				<mat-icon>call_merge</mat-icon>
			</div>
		</div>
	</div>

	<!-- Regular Field Node -->
	<div class="field-node-wrapper" *ngIf="node.nodeType === 'field'">
		<div class="flow-connector-vertical"></div>
		<ng-container *ngTemplateOutlet="fieldNodeTemplate; context: { node: node }"></ng-container>
	</div>
</ng-template>

<!-- Field Node Template -->
<ng-template #fieldNodeTemplate let-node="node">
	<div
		class="flow-node-wrapper"
		[class.is-group]="node.fieldType === 'group'"
		[class.is-repeater]="node.fieldType === 'repeater' || node.isRepeater"
		[style.margin-left.px]="node.level * 20"
	>
		<!-- Group Container -->
		<div class="group-container" *ngIf="node.fieldType === 'group' && node.children.length > 0">
			<!-- Group Header -->
			<div
				class="flow-node field-node group-header"
				[class.selected]="isNodeSelected(node)"
				[style.border-left-color]="getNodeColor(node.fieldType)"
				(click)="selectNode(node)"
			>
				<div class="node-header">
					<mat-icon class="node-icon" [style.color]="getNodeColor(node.fieldType)">
						{{ getFieldIcon(node.fieldType || '') }}
					</mat-icon>
					<div class="node-info">
						<div class="node-label">{{ node.label }}</div>
						<div class="node-meta">
							<span class="node-type">{{ node.fieldType }}</span>
							<span class="node-key" *ngIf="node.key">{{ node.key }}</span>
							<span class="group-count-badge">
								<mat-icon>grid_view</mat-icon>
								{{ node.children.length }} fields
							</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Group Content -->
			<div class="group-content">
				<ng-container *ngFor="let childNode of node.children">
					<ng-container *ngTemplateOutlet="fieldNodeTemplate; context: { node: childNode }"></ng-container>
				</ng-container>
			</div>
		</div>

		<!-- Repeater Container -->
		<div
			class="repeater-container"
			*ngIf="(node.fieldType === 'repeater' || node.isRepeater) && node.children.length > 0"
		>
			<!-- Repeater Header -->
			<div
				class="flow-node field-node repeater-header"
				[class.selected]="isNodeSelected(node)"
				[style.border-left-color]="getNodeColor(node.fieldType)"
				(click)="selectNode(node)"
			>
				<div class="node-header">
					<div class="repeater-icon-wrapper">
						<mat-icon class="node-icon" [style.color]="getNodeColor(node.fieldType)">
							{{ getFieldIcon(node.fieldType || '') }}
						</mat-icon>
						<mat-icon class="repeat-indicator">repeat</mat-icon>
					</div>
					<div class="node-info">
						<div class="node-label">{{ node.label }}</div>
						<div class="node-meta">
							<span class="node-type">{{ node.fieldType }}</span>
							<span class="node-key" *ngIf="node.key">{{ node.key }}</span>
							<span class="repeater-badge">
								<mat-icon>loop</mat-icon>
								Repeatable Section
							</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Repeater Content -->
			<div class="repeater-content">
				<div class="repeater-iteration-label">
					<mat-icon>playlist_add</mat-icon>
					<span>Each iteration contains:</span>
				</div>
				<ng-container *ngFor="let childNode of node.children">
					<ng-container *ngTemplateOutlet="fieldNodeTemplate; context: { node: childNode }"></ng-container>
				</ng-container>
			</div>
		</div>

		<!-- Regular Field Node (not group or repeater with children) -->
		<div
			class="flow-node field-node"
			*ngIf="
				(node.fieldType !== 'group' && node.fieldType !== 'repeater' && !node.isRepeater) ||
				node.children.length === 0
			"
			[class.selected]="isNodeSelected(node)"
			[style.border-left-color]="getNodeColor(node.fieldType)"
			(click)="selectNode(node)"
		>
			<div class="node-header">
				<mat-icon class="node-icon" [style.color]="getNodeColor(node.fieldType)">
					{{ getFieldIcon(node.fieldType || '') }}
				</mat-icon>
				<div class="node-info">
					<div class="node-label">{{ node.label }}</div>
					<div class="node-meta">
						<span class="node-type">{{ node.fieldType }}</span>
						<span class="node-key" *ngIf="node.key">{{ node.key }}</span>
						<span class="repeater-badge" *ngIf="node.isRepeater">
							<mat-icon>repeat</mat-icon>
							Repeater
						</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Child Nodes for non-group/repeater fields -->
		<div
			class="node-children"
			*ngIf="
				node.children.length > 0 &&
				node.fieldType !== 'group' &&
				node.fieldType !== 'repeater' &&
				!node.isRepeater
			"
		>
			<div class="children-label">
				<mat-icon>subdirectory_arrow_right</mat-icon>
				<span>Nested Fields ({{ node.children.length }})</span>
			</div>
			<ng-container *ngFor="let childNode of node.children">
				<ng-container *ngTemplateOutlet="fieldNodeTemplate; context: { node: childNode }"></ng-container>
			</ng-container>
		</div>
	</div>
</ng-template>
